import { useEffect, useState } from "react";
import Highcharts, { color } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  Box, Button, Checkbox, Grid2, ListItemText, MenuItem, Popover,
  Select, Skeleton, Typography, useTheme
} from "@mui/material";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { useParams } from "react-router-dom";
import { useGetManpowerHistogramMutation } from "store/slices/apis/manpowerDashboardSlice";
import { fontWeight } from "@mui/system";
import { ReactComponent as ChevronDownIcon } from "assets/PwC_Funct_Icons_ChevronDown_Outline_Black_RGB.svg"
import { ReactComponent as ChevronUpIcon } from "assets/PwC_Funct_Icons_ChevronUp_Outline_Black_RGB.svg"

const ManpowerHistogram = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [menuCategory, setMenuCategory] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [isStack, setIsStack] = useState("normal");
  const [selectedYear, setSelectedYear] = useState("2025");
  const [isLoading, setIsLoading] = useState(false);
  const [seriesData, setSeriesData] = useState([]);
  const [xAxis, setxAxis] = useState([]);

  const [getManpowerHistogram, { data }] = useGetManpowerHistogramMutation();

  useEffect(() => {
    fetchManpowerHistogramData();
  }, []);

  const fetchManpowerHistogramData = async () => {
    setIsLoading(true);
    try {
      const res = await getManpowerHistogram({
        body: [],
        params: { projectId }
      });

      if (res?.data?.data) {
        const rawData = res.data.data;

        const uniqueMonths = [...new Set(rawData.map(item => item?.month))];
        const uniqueCategories = [...new Set(rawData.map(item => item?.category))];
        const allCategories = ["All Category", ...uniqueCategories];

        setMenuCategory(allCategories);
        setSelectedCategories(allCategories);

        const constructedSeries = constructSeriesFromAPI(rawData, uniqueCategories, uniqueMonths);
        setGraphData({ fullSeries: constructedSeries, xAxisCategories: uniqueMonths });
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching graph data", error);
      setIsLoading(false);
    }
  };

  const CustomChevronIcon = (props) => (
    <ChevronDownIcon style={{ width: '16px', height: '16px' }} {...props} />
  );




  const constructSeriesFromAPI = (data, categories, months) => {
    const series = [];


    const colorPalette = [
      '#D04A02', '#EB8C00', '#FFB600', '#7D7D7D',
      '#DEDEDE', '#295477', '#E72B33', '#299D8F',
      '#4577C9', '#00C6F7', '#D93954', '#657DEF',
    ];


    const categoryColorMap = {};
    categories.forEach((category, index) => {
      categoryColorMap[category] = colorPalette[index % colorPalette.length];
    });


    categories.forEach((category) => {
      const color = categoryColorMap[category];

      const planData = months.map(month => {
        const entry = data.find(d => d.month === month && d.category === category && d.type === "Plan");
        return entry ? + entry.manpowerCount : 0;
      });

      const actualData = months.map(month => {
        const entry = data.find(d => d.month === month && d.category === category && d.type === "Actual");
        return entry ? + entry.manpowerCount : 0;
      });

      series.push({
        name: category,
        color,
        data: planData,
        stack: "Plan",
        showInLegend: true,
        marker: { symbol: 'square' },
        xAxis: 1,
        dataLabels: { enabled: false }
      });

      series.push({
        name: category,
        color,
        data: actualData,
        stack: "Actual",
        showInLegend: false,
        marker: { symbol: 'square' },
        xAxis: 1,
        dataLabels: { enabled: false }
      });
    });

    return series;
  };


  const constructCats = (monthsLength) => {
    return Array.from({ length: monthsLength }, () => ['Plan', 'Actual']).flat();
  };

  useEffect(() => {
    if (graphData?.xAxisCategories?.length > 0) {
      setxAxis([
        {
          categories: constructCats(graphData.xAxisCategories.length),
          labels: {
            style: { color: '#00000080', fontSize: '12px', fontWeight: 500 }
          },
          lineWidth: 0,
          tickWidth: 0
        },
        {
          categories: graphData.xAxisCategories,
          labels: {
            style: { color: '#464646', fontWeight: 500, fontSize: '13px' }
          },
          lineWidth: 0,
          tickWidth: 0
        }
      ]);
    }
  }, [graphData]);


  useEffect(() => {
    if (graphData?.fullSeries) {
      setSeriesData(graphData.fullSeries);
    }
  }, [graphData]);


  const options = {
    chart: { type: "column", height: 550 },
    title: { text: "" },
    xAxis: [
      {
        categories: constructCats(graphData?.xAxisCategories?.length || 0),
        labels: {
          style: { color: '#00000080', fontSize: '12px', fontWeight: 500 }
        },
        lineWidth: 1,
        tickWidth: 1,
      },
      {
        categories: graphData?.xAxisCategories || [],
        labels: {
          style: { fontSize: '10px', fontWeight: 500, color: "#414D55" }
        },
        lineWidth: 1,
        lineColor: "#758195",
        tickWidth: 1,
        tickColor: "#758195",
        tickmarkPlacement: "on",
      }
    ],
    yAxis: {
      allowDecimals: false,
      min: 0,
      title: {
        text: 'Mandays / Day / Month',
        style: { fontSize: '14px', fontWeight: 500, color: "#415385" }
      },
      labels: {
        formatter: function () {
          return this.value;
        },
        style: { fontSize: '10px', fontWeight: 500, color: "#414D55" }
      },
      lineWidth: 1,
      lineColor: "#758195",
      tickWidth: 1,
      tickColor: "#758195"
    },

    // tooltip: {
    //   formatter: function () {
    //     return `<b>${this.x} ${this.series.options.stack}</b><br/>${this.series.name}: ${this.y}<br/>Total: ${this.point.stackTotal}`;
    //   }
    // },
    tooltip: {
      formatter: function () {
        return `<b>${this.x} ${this.series.options.stack}</b><br/>
            ${this.series.name}: ${this.y.toFixed(2)}<br/>
            Total: ${this.point.stackTotal?.toFixed(2)}`;
      }
    },
    legend: {
      symbolHeight: 17,
      symbolWidth: 17,
      symbolRadius: 0,
      layout: 'horizontal',
      align: 'left',
      verticalAlign: 'bottom',
      itemDistance: 20,
      itemWidth: 200,
      width: 1000,
      x: 100,
      itemStyle: {
        fontSize: '14px',
        color: '#464646B2',
        fontWeight: 500
      }
    },

    plotOptions: {
      column: { stacking: isStack, borderWidth: 0, pointWidth: 40,  },
      series: {
        events: {
          legendItemClick: function () {
            const myName = this.name;
            this.chart.series.forEach((s) => {
              if (s.name === myName) s.setVisible(!this.visible, false);
            });
            this.chart.redraw();
            return false;
          }
        }
      }
    },
    series: seriesData,
    credits: { enabled: false },
  };


  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };


  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleCheckboxChange = (category) => {
    if (category === "All Category") {
      const isAllSelected = selectedCategories.length === menuCategory.length;
      setSelectedCategories(isAllSelected ? [] : menuCategory);
    } else {
      setSelectedCategories((prev) => {
        let newSelected;
        if (prev.includes(category)) {
          newSelected = prev.filter((item) => item !== category);
        } else {
          newSelected = [...prev, category];
        }
        if (newSelected.includes("All Category") && newSelected.length !== menuCategory.length) {
          newSelected = newSelected.filter(item => item !== "All Category");
        }
        const allWithoutAllCategory = menuCategory.filter(cat => cat !== "All Category");
        const allSelected = allWithoutAllCategory.every(cat => newSelected.includes(cat));
        if (allSelected && !newSelected.includes("All Category")) {
          newSelected.push("All Category");
        }
        return newSelected;
      });
    }
  };


  const handleChangeCategory = (newSelectedCategories) => {
    if (!graphData?.fullSeries) return;
    if (newSelectedCategories?.length > 1) {
      let filteredData = graphData?.fullSeries.filter(
        (val) => newSelectedCategories.includes(val?.name) || val.name === ""
      );
      setSeriesData(filteredData);
      setIsStack("false");
    } else {
      let actualPlanGraph = [];
      let filteredData = graphData?.fullSeries.filter(
        (val) => newSelectedCategories.includes(val?.name)
      );

      filteredData?.map((value) => {
        return actualPlanGraph.push({
          name: value?.stack,
          data: value?.data,
          color: value?.stack === "Plan" ? "#415385" : "#CE412D",
          borderRadiusTopLeft: 5,
          borderRadiusTopRight: 5,
          borderRadiusBottomLeft: 5,
          borderRadiusBottomRight: 5,
          showInLegend: true,
          dataLabels: {
            shadow: false,
            enabled: true,
            inside: true,
            verticalAlign: 'top',
            format: '{point.y:.2f}',
            y: 10,
            style: {
              textOutline: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#FFFFFF'
            }
          }
        });
      });

      setSeriesData(() => [...actualPlanGraph])
      setIsStack(false);
      handleClose();
      return true
    }


  };

  return (
    <Box sx={{ padding: 2, background: "#fff", borderRadius: 2, height: "100%", border: "1px solid #2525252E" }}>
      <Grid2 container>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Typography
            variant="body"
            sx={{
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: "100%",
              letterSpacing: "-0.4px",
              color: `${theme?.customSettings?.themeColor}`,
            }}
          >
            Manpower Histogram
          </Typography>
          {/* <Typography
            sx={{
              mt: "5px",
              color: "#5052588A",
              fontSize: "14px",
              mb: "20px",
              lineHeight: "100%",
              letterSpacing: "-0.4px",
            }}
          >
            Get a holistic view of how work is being prioritized
          </Typography> */}
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box sx={{ width: '100%', display: "flex", justifyContent: 'end', mt: "14px" }}>
            <div>
              <Button
                variant="contained"
                onClick={handleClick}
                // endIcon={anchorEl ? <IoIosArrowUp style={{ fontSize: '16px' }} /> : <IoIosArrowDown style={{ fontSize: '16px' }} />}
                endIcon={
                  anchorEl ? (
                    <ChevronUpIcon style={{ width: "16px", height: "16px" }} />
                  ) : (
                    <ChevronDownIcon style={{ width: "16px", height: "16px" }} />
                  )
                }

                sx={{
                  backgroundColor: "#E8E8E8",
                  color: "#474747",
                  textTransform: "none",
                  borderRadius: "5px",
                  padding: "6px 12px",
                  boxShadow: 'none',
                  lineHeight: '24px',
                  letterSpacing: '-0.4px'
                }}
              >
                All Category
              </Button>

              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left"
                }}
              >

                <div style={{ padding: "10px", minWidth: "200px" }}>
                  {menuCategory.map((category) => (
                    <MenuItem key={category} sx={{
                      padding: 0,
                      color: '#474747',
                      fontWeight: 400,
                      lineHeight: '24px',
                      letterSpacing: '-0.4px'
                    }}>
                      <Checkbox
                        checked={selectedCategories.includes(category)}
                        onChange={() => handleCheckboxChange(category)}
                        sx={{
                          padding: '5px'
                        }}

                      />
                      <ListItemText primary={category} />
                    </MenuItem>
                  ))}

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleChangeCategory(selectedCategories)}
                    sx={{
                      backgroundColor: `${theme?.customSettings?.themeColor}`,
                      color: "#fff",
                      fontWeight: '500',
                      fontSize: '16px',
                      lineHeight: '24px',
                      letterSpacing: '-0.4px',
                      marginTop: "10px",
                      textTransform: 'none'
                    }}
                  >
                    Apply Filter
                  </Button>
                </div>

              </Popover>
            </div>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              // IconComponent={IoIosArrowDown}
              IconComponent={CustomChevronIcon}
              sx={{
                marginLeft: "30px",
                backgroundColor: "#E8E8E8",
                color: "#474747",
                textTransform: "none",
                boxShadow: "none",
                lineHeight: "24px",
                letterSpacing: "-0.4px",
                "& .MuiOutlinedInput-input": {
                  padding: "5px 15px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="2025">2025</MenuItem>
              <MenuItem value="2024">2024</MenuItem>
            </Select>
          </Box>
        </Grid2>
      </Grid2>

      <Grid2 xs={12} sx={{ mt: "40px" }}>
        {isLoading ? (
          <Skeleton variant="rectangular" height={300} animation="wave" />
        ) : seriesData.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 500,
              mt: 2,
              color: "#9E9E9E",
              fontSize: "16px",
            }}
          >
            No data available to display the chart.
          </Box>

        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ width: `${graphData?.xAxisCategories?.length * 80}px` }}>
              <HighchartsReact highcharts={Highcharts} options={options} />
            </Box>
          </Box>

        )}
      </Grid2>
    </Box >
  );
};

export default ManpowerHistogram;
